/**
 * Puente entre los interceptores de axios y las notificaciones de React.
 *
 * Los interceptores viven fuera del arbol de componentes, asi que no pueden
 * llamar a `useNotifications()`. Publican aqui y NotificationProvider se
 * suscribe. Sin esto, un 403 del backend se quedaba en un console.error y la
 * pantalla se veia simplemente vacia: el usuario no sabia si no habia datos o
 * si le faltaba un permiso.
 */

const listeners = new Set();

// Evita repetir el mismo aviso cuando una pantalla lanza varias peticiones a la
// vez y todas devuelven 403 (por ejemplo una lista y sus estadisticas).
let lastNotice = { key: null, at: 0 };
const DEDUPE_MS = 3000;

export const onPermissionError = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Traduce el 403 del backend a un aviso entendible.
 * `detail` suele ser "Insufficient permissions" o un texto del endpoint.
 */
export const emitPermissionError = ({ url, detail } = {}) => {
  const recurso = describirRecurso(url);
  const key = `${recurso}|${detail || ''}`;
  const ahora = Date.now();

  if (lastNotice.key === key && ahora - lastNotice.at < DEDUPE_MS) return;
  lastNotice = { key, at: ahora };

  const generico = !detail || /insufficient permissions/i.test(detail);
  const message = generico
    ? `No tienes permiso para ${recurso}. Pide a un administrador que anada el permiso correspondiente a tu rol.`
    : detail;

  listeners.forEach((listener) => listener({ message, url, detail }));
};

/** Convierte la ruta de la API en algo legible para el usuario. */
const describirRecurso = (url = '') => {
  const ruta = String(url).replace(/^https?:\/\/[^/]+/, '');
  const mapa = [
    [/\/clients/, 'consultar clientes'],
    [/\/products/, 'consultar productos'],
    [/\/inventory/, 'consultar el inventario'],
    [/\/suppliers/, 'consultar proveedores'],
    [/\/purchase-orders/, 'consultar ordenes de compra'],
    [/\/production-processes|\/processes/, 'consultar procesos de produccion'],
    [/\/formulas/, 'consultar formulaciones'],
    [/\/prospects/, 'consultar prospectos'],
    [/\/invoices/, 'consultar facturacion'],
    [/\/sales/, 'consultar ventas'],
    [/\/reports/, 'consultar reportes'],
    [/\/admin\/settings/, 'ver la configuracion del sistema'],
    [/\/permissions/, 'gestionar permisos'],
    [/\/roles/, 'gestionar roles'],
    [/\/users/, 'gestionar usuarios'],
  ];

  const encontrado = mapa.find(([patron]) => patron.test(ruta));
  return encontrado ? encontrado[1] : 'realizar esta accion';
};
